import React, { useState } from "react";
import { apiClient } from "@/api/apiClient";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, Check, AlertCircle, Shield, User, Clock, Bell, Settings, Trash2, Sun, Moon, Monitor, Bot } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { useNavigate } from "react-router-dom"; // Assuming react-router-dom for useNavigate
import { pushManager } from "@/utils/pushManager";
import { useTheme } from "@/contexts/ThemeContext";

export default function AccountManagement({ user }) {
    const navigate = useNavigate(); // Added useNavigate hook
    const queryClient = useQueryClient();
    const [email, setEmail] = useState(user?.email || "");
    const [isEditing, setIsEditing] = useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false); // Added state for delete dialog
    const [showHistoryResetDialog, setShowHistoryResetDialog] = useState(false);
    const [showPasswordResetDialog, setShowPasswordResetDialog] = useState(false);
    const [showGuestResetDialog, setShowGuestResetDialog] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [emailCooldown, setEmailCooldown] = useState(0); // Cooldown in seconds
    const [logoutCountdown, setLogoutCountdown] = useState(null); // specific countdown for logout

    // Parse existing sizes or empty
    const initialSizes = user?.sizes && Array.isArray(user.sizes) ? user.sizes.join(', ') : '';
    const [sizesInput, setSizesInput] = useState(initialSizes);
    const [isEditingSizes, setIsEditingSizes] = useState(false);

    // AI Max Cards
    const initialAiMaxCards = user?.ai_max_cards || 4;
    const [aiMaxCardsInput, setAiMaxCardsInput] = useState(initialAiMaxCards);
    const [isEditingAiMaxCards, setIsEditingAiMaxCards] = useState(false);

    React.useEffect(() => {
        if (user?.sizes && Array.isArray(user.sizes)) {
            setSizesInput(user.sizes.join(', '));
        } else if (user && !user.sizes) {
            setSizesInput('');
        }
        
        if (user?.ai_max_cards) {
            setAiMaxCardsInput(user.ai_max_cards);
        }
    }, [user]);

    // Queries for preferences and subscriptions
    const { data: preferences, isLoading: prefsLoading } = useQuery({
        queryKey: ['notificationPreferences'],
        queryFn: () => apiClient.notifications.getPreferences(),
        enabled: !!user?.id
    });

    const { data: subscriptions, isLoading: subsLoading } = useQuery({
        queryKey: ['brandSubscriptions'],
        queryFn: () => apiClient.notifications.getSubscriptions(),
        enabled: !!user?.id
    });

    const updatePrefsMutation = useMutation({
        mutationFn: (newPrefs) => apiClient.notifications.updatePreferences(newPrefs),
        onSuccess: () => {
            queryClient.invalidateQueries(['notificationPreferences']);
            toast.success("Préférences de notification mises à jour");
        },
        onError: () => toast.error("Erreur lors de la mise à jour des préférences")
    });

    const unsubscribeMutation = useMutation({
        mutationFn: (siteId) => apiClient.notifications.unsubscribe(siteId),
        onSuccess: () => {
            queryClient.invalidateQueries(['brandSubscriptions']);
            toast.success("Abonnement annulé");
        }
    });

    const updateSizesMutation = useMutation({
        mutationFn: (sizesArr) => apiClient.auth.updateProfile({ sizes: sizesArr }),
        onSuccess: () => {
            queryClient.invalidateQueries(['user']);
            toast.success("Tailles mises à jour");
            setIsEditingSizes(false);
        },
        onError: () => toast.error("Erreur lors de la mise à jour des tailles")
    });

    const handleSaveSizes = () => {
        const sizesArr = sizesInput.split(',').map(s => s.trim()).filter(s => s);
        updateSizesMutation.mutate(sizesArr);
    };

    const updateAiMaxCardsMutation = useMutation({
        mutationFn: (cards) => apiClient.auth.updateProfile({ ai_max_cards: cards }),
        onSuccess: () => {
            queryClient.invalidateQueries(['user']);
            toast.success("Paramètre IA mis à jour");
            setIsEditingAiMaxCards(false);
        },
        onError: () => toast.error("Erreur lors de la mise à jour")
    });

    const handleSaveAiMaxCards = () => {
        const val = parseInt(aiMaxCardsInput, 10);
        if (isNaN(val) || val < 1 || val > 10) {
            toast.error("Veuillez entrer un nombre entre 1 et 10");
            return;
        }
        updateAiMaxCardsMutation.mutate(val);
    };

    const handleTogglePref = (key, checked) => {
        if (!preferences) return;
        updatePrefsMutation.mutate({ [key]: checked });
    };

    const handleTogglePush = async (checked) => {
        if (!preferences) return;

        try {
            if (checked) {
                // Subscribe
                const success = await pushManager.subscribe();
                if (success) {
                    updatePrefsMutation.mutate({ push_enabled: true });
                }
            } else {
                // Unsubscribe
                await pushManager.unsubscribe();
                updatePrefsMutation.mutate({ push_enabled: false });
            }
        } catch (error) {
            toast.error("Impossible de modifier les notifications push. Vérifiez que vous les avez autorisées dans votre navigateur.");
        }
    };

    const handleTestPush = async () => {
        try {
            await apiClient.notifications.testPush();
            toast.success("Notification de test envoyée !");
        } catch (error) {
            toast.error("Erreur lors de l'envoi du test");
        }
    };

    const handleGuestResetConfirm = () => {
        localStorage.removeItem('guest_interactions');
        localStorage.removeItem('guestCategories');
        localStorage.removeItem('swipeCurrentIndex');
        // Critical: Remove guest_id so a new one is generated, effectively resetting the backend history for this user
        localStorage.removeItem('guest_id');
        // Also clear local storage for products to force a fresh fetch
        localStorage.removeItem('swipeProducts');
        toast.success("Préférences réinitialisées !");
        setShowGuestResetDialog(false);
        window.location.reload();
    };

    React.useEffect(() => {
        let interval;
        if (emailCooldown > 0) {
            interval = setInterval(() => {
                setEmailCooldown((prev) => prev - 1);
            }, 1000);
        }
        return () => clearInterval(interval);
    }, [emailCooldown]);

    React.useEffect(() => {
        if (logoutCountdown === null) return;

        if (logoutCountdown > 0) {
            const timer = setTimeout(() => setLogoutCountdown(logoutCountdown - 1), 1000);
            return () => clearTimeout(timer);
        } else {
            // Countdown finished, perform logout
            (async () => {
                await apiClient.auth.logout();
                queryClient.invalidateQueries({ queryKey: ['user'] });
                queryClient.setQueryData(['user'], null);
                navigate('/login');
            })();
        }
    }, [logoutCountdown, navigate]);

    // Update Profile Mutation (for Email)
    const updateProfileMutation = useMutation({
        mutationFn: async (data) => {
            return apiClient.auth.updateProfile(data);
        },
        onSuccess: async () => {
            // If email was changed, we must logout and force re-login
            if (isEditing) { // isEditing is true when we are in the email form
                toast.success("Email mis à jour. Déconnexion dans 5 secondes...");
                setLogoutCountdown(5);
            } else {
                queryClient.invalidateQueries(['user']);
                toast.success("Profil mis à jour");
            }
            setIsEditing(false);
            setShowEmailConfirmDialog(false);
        },
        onError: (error) => {
            console.error(error);
            toast.error(error.response?.data?.error || "Erreur lors de la mise à jour");
            setShowEmailConfirmDialog(false);
        }
    });

    const [showEmailConfirmDialog, setShowEmailConfirmDialog] = useState(false);
    const [pendingEmail, setPendingEmail] = useState("");

    const handleSaveEmail = () => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            toast.error("Format d'email invalide (ex: nom@domaine.com)");
            return;
        }

        if (email === user.email) {
            setIsEditing(false);
            return;
        }

        setPendingEmail(email);
        setShowEmailConfirmDialog(true);
    };

    const confirmEmailChange = () => {
        updateProfileMutation.mutate({ email: pendingEmail });
    };

    // Password Reset Trigger (Mocked for now as we might need a specific endpoint)
    const resetPasswordMutation = useMutation({
        mutationFn: async () => {
            // In a real app this might trigger a reset email or redirect
            // For now we'll assumes a request to send a reset email
            return apiClient.auth.requestPasswordReset({ email: user.email });
        },
        onSuccess: () => {
            toast.success("Email de réinitialisation envoyé");
        },
        onError: () => {
            // Fallback if specific endpoint fails or is different, just show a message for the prototype
            toast.info("Veuillez vérifier vos emails pour la procédure.");
        }
    });

    const handlePasswordReset = () => {
        setShowPasswordResetDialog(true);
    };

    const confirmPasswordReset = () => {
        resetPasswordMutation.mutate();
        setShowPasswordResetDialog(false);
    };

    const handleDeleteAccount = async () => {
        setIsLoading(true);
        try {
            await apiClient.auth.scheduleDeletion();
            toast.success("Compte programmé pour suppression dans 7 jours");
            setShowDeleteDialog(false);
            // Logout and redirect
            await apiClient.auth.logout();
            queryClient.invalidateQueries({ queryKey: ['user'] });
            queryClient.setQueryData(['user'], null);
            navigate('/login');
        } catch (error) {
            // console.error(error);
            toast.error("Erreur lors de la programmation de la suppression");
        } finally {
            setIsLoading(false);
        }
    };

    const handleResetHistory = async () => {
        setIsLoading(true);
        try {
            await apiClient.auth.resetHistory();
            // Also clear local guest data
            localStorage.removeItem('guest_interactions');
            localStorage.removeItem('guestCategories');
            localStorage.removeItem('swipeCurrentIndex');
            // Invalidate all related queries so they refetch with filtered data
            queryClient.invalidateQueries({ queryKey: ['interactions'] });
            queryClient.invalidateQueries({ queryKey: ['userCategories'] });
            toast.success("Historique réinitialisé !");
            setShowHistoryResetDialog(false);
            // Reload to reflect empty history
            window.location.reload();
        } catch (error) {
            // console.error(error);
            toast.error("Erreur lors de la réinitialisation");
        } finally {
            setIsLoading(false);
        }
    };

    // Format date
    const memberSince = user?.created_at
        ? new Date(user.created_at).toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric' })
        : 'Inconnu';

    return (
        <div className="space-y-6">
            <Tabs defaultValue="account" className="w-full">
                <TabsList className="grid w-full grid-cols-2 mb-8 bg-amber-50 dark:bg-amber-950/40 p-1 rounded-xl">
                    <TabsTrigger value="account" className="rounded-lg data-[state=active]:bg-white dark:data-[state=active]:bg-[hsl(225,14%,14%)] data-[state=active]:text-amber-600 dark:data-[state=active]:text-amber-400 data-[state=active]:shadow-sm">Compte & Sécurité</TabsTrigger>
                    <TabsTrigger value="preferences" className="rounded-lg data-[state=active]:bg-white dark:data-[state=active]:bg-[hsl(225,14%,14%)] data-[state=active]:text-amber-600 dark:data-[state=active]:text-amber-400 data-[state=active]:shadow-sm">Préférences</TabsTrigger>
                </TabsList>

                <TabsContent value="account">
                    {/* Guest State Check */}
                    {!user?.id ? (
                        <Card className="border-amber-100 dark:border-amber-900/40 bg-amber-50/50 dark:bg-amber-950/20">
                            <CardHeader>
                                <CardTitle className="text-amber-900 dark:text-amber-300">Mode Invité</CardTitle>
                                <CardDescription>
                                    Vous naviguez actuellement en tant qu'invité. Pour gérer votre profil, sauvegarder vos likes et modifier vos informations, veuillez créer un compte.
                                </CardDescription>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-3">
                                    <Button
                                        className="w-full bg-amber-600 hover:bg-amber-700 text-white shadow-sm"
                                        onClick={() => navigate('/register')}
                                    >
                                        Créer un compte pour sauvegarder
                                    </Button>
                                    <div className="text-center text-sm text-gray-500">
                                        <span>Déjà membre ? </span>
                                        <span
                                            className="font-semibold text-amber-600 dark:text-amber-400 cursor-pointer hover:underline"
                                            onClick={() => navigate('/login')}
                                        >
                                            Se connecter
                                        </span>
                                    </div>

                                    <div className="border-t pt-4 mt-4">
                                        <p className="text-xs text-gray-500 mb-2 text-center">
                                            Vous n'aimez pas vos recommandations ?
                                        </p>
                                        <Button
                                            variant="outline"
                                            className="w-full text-red-500 hover:text-red-600 hover:bg-red-50 border-red-200"
                                            onClick={() => setShowGuestResetDialog(true)}
                                        >
                                            Réinitialiser mes préférences (Invité)
                                        </Button>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    ) : (
                        <div className="space-y-6">
                            <Card>
                                <CardHeader>
                                    <div className="flex items-center gap-2">
                                        <User className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                                        <CardTitle>Mes Informations</CardTitle>
                                    </div>
                                    <CardDescription>Gérez vos identifiants de connexion</CardDescription>
                                </CardHeader>
                                <CardContent className="space-y-6">
                                    {/* Email */}
                                    <div className="space-y-2">
                                        <Label htmlFor="email">Email</Label>
                                        <div className="flex gap-2">
                                            <Input
                                                id="email"
                                                value={email}
                                                onChange={(e) => setEmail(e.target.value)}
                                                disabled={!isEditing}
                                                className={!isEditing ? "bg-gray-50 dark:bg-gray-900/50 font-medium" : ""}
                                            />
                                            {isEditing ? (
                                                <div className="flex gap-2">
                                                    <Button
                                                        onClick={handleSaveEmail}
                                                        disabled={updateProfileMutation.isPending}
                                                        className="bg-green-600 hover:bg-green-700 text-white"
                                                    >
                                                        {updateProfileMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                                                    </Button>
                                                    <Button
                                                        variant="outline"
                                                        onClick={() => {
                                                            setIsEditing(false);
                                                            setEmail(user?.email || "");
                                                        }}
                                                    >
                                                        <XLogIcon className="w-4 h-4" />
                                                        Annuler
                                                    </Button>
                                                </div>
                                            ) : (
                                                <Button variant="outline" onClick={() => setIsEditing(true)}>
                                                    Modifier
                                                </Button>
                                            )}
                                        </div>
                                        <div className="flex items-center gap-2 mt-1">
                                            {user?.is_email_verified ? (
                                                <Badge variant="secondary" className="bg-green-100 text-green-700 hover:bg-green-100 whitespace-nowrap">
                                                    <Check className="w-3 h-3 mr-1" /> Vérifié
                                                </Badge>
                                            ) : (
                                                <Badge variant="secondary" className="bg-yellow-100 text-yellow-700 hover:bg-yellow-100 whitespace-nowrap cursor-pointer">
                                                    <AlertCircle className="w-3 h-3 mr-1" /> Non vérifié
                                                </Badge>
                                            )}
                                            {!user?.is_email_verified && (
                                                emailCooldown > 0 ? (
                                                    <span className="text-xs text-gray-400 cursor-not-allowed">
                                                        Renvoyer dans {emailCooldown}s
                                                    </span>
                                                ) : (
                                                    <span
                                                        className="text-xs text-amber-600 hover:underline cursor-pointer"
                                                        onClick={() => {
                                                            // Trigger logic to resend email (mocked or real)
                                                            toast.success("Email de vérification envoyé");
                                                            setEmailCooldown(60); // 60 seconds cooldown
                                                        }}
                                                    >
                                                        Renvoyer l'email de vérification
                                                    </span>
                                                )
                                            )}
                                        </div>
                                    </div>

                                    {/* Password */}
                                    <div className="space-y-2 pt-2 border-t">
                                        <div className="flex items-center justify-between mt-4">
                                            <div>
                                                <Label className="block mb-1">Mot de passe</Label>

                                            </div>
                                            <Button variant="outline" onClick={handlePasswordReset}>
                                                Changer le mot de passe
                                            </Button>
                                        </div>
                                    </div>

                                    {/* Member Since */}
                                    <div className="flex items-center gap-2 text-sm text-gray-500 pt-4 border-t mt-4">
                                        <Clock className="w-4 h-4" />
                                        <span>Membre depuis le {memberSince}</span>
                                    </div>
                                </CardContent>
                            </Card>

                            {/* Danger Zone */}
                            <Card className="border-red-100 dark:border-red-900/30 bg-red-50/50 dark:bg-red-900/10">
                                <CardHeader>
                                    <div className="flex items-center gap-2">
                                        <Shield className="w-5 h-5 text-red-500" />
                                        <CardTitle className="text-red-700 dark:text-red-400">Réinitialiser / Supprimer le compte</CardTitle>
                                    </div>
                                    <CardDescription className="text-red-600/80 dark:text-red-400/80">
                                        Vous souhaitez repartir de zéro ou nous quitter ?
                                    </CardDescription>
                                </CardHeader>
                                <CardContent>
                                    <div className="space-y-4">
                                        <p className="text-sm text-gray-600">
                                            Cette action désactive votre compte immédiatement. Pour le réinitialiser (repartir à neuf) ou le supprimer définitivement, laissez-le inactif pendant <strong>7 jours</strong>.
                                            <br />
                                            Vous pouvez tout récupérer simplement en vous reconnectant avant ce délai.
                                        </p>
                                        <div className="flex flex-col sm:flex-row gap-3">
                                            <Button
                                                className="w-full sm:w-auto bg-orange-500 hover:bg-orange-600 text-white"
                                                onClick={() => setShowHistoryResetDialog(true)}
                                            >
                                                Réinitialiser uniquement l'historique
                                            </Button>
                                            <Button
                                                variant="destructive"
                                                className="w-full sm:w-auto"
                                                onClick={() => setShowDeleteDialog(true)}
                                            >
                                                Supprimer mon compte
                                            </Button>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        </div>
                    )}
                </TabsContent>

                <TabsContent value="preferences">
                    <div className="space-y-6">
                        {/* Apparence / Mode Sombre */}
                        <ThemeCard />
                        {/* Tailles Préférées */}
                        <Card>
                            <CardHeader>
                                <div className="flex items-center gap-2">
                                    <Settings className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                                    <CardTitle>Mes Tailles</CardTitle>
                                </div>
                                <CardDescription>Définissez vos tailles pour les alertes de restock (ex: S, M, 42)</CardDescription>
                            </CardHeader>
                            <CardContent>
                                <div className="flex gap-2">
                                    <Input
                                        placeholder="Ex: S, M, 38, 42"
                                        value={sizesInput}
                                        onChange={(e) => setSizesInput(e.target.value)}
                                        disabled={!isEditingSizes}
                                        className={!isEditingSizes ? "bg-gray-50 dark:bg-gray-900/50 font-medium" : ""}
                                    />
                                    {isEditingSizes ? (
                                        <div className="flex gap-2">
                                            <Button
                                                onClick={handleSaveSizes}
                                                disabled={updateSizesMutation.isPending}
                                                className="bg-green-600 hover:bg-green-700 text-white"
                                            >
                                                {updateSizesMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                                            </Button>
                                            <Button variant="outline" onClick={() => {
                                                setIsEditingSizes(false);
                                                setSizesInput(initialSizes);
                                            }}>
                                                Annuler
                                            </Button>
                                        </div>
                                    ) : (
                                        <Button variant="outline" onClick={() => setIsEditingSizes(true)}>
                                            Modifier
                                        </Button>
                                    )}
                                </div>
                            </CardContent>
                        </Card>

                        {/* Paramètre IA */}
                        <Card>
                            <CardHeader>
                                <div className="flex items-center gap-2">
                                    <Bot className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                                    <CardTitle>Personal Shopper IA</CardTitle>
                                </div>
                                <CardDescription>Nombre de vêtements suggérés par l'IA (entre 1 et 10)</CardDescription>
                            </CardHeader>
                            <CardContent>
                                <div className="flex gap-2">
                                    <Input
                                        type="number"
                                        min="1"
                                        max="10"
                                        value={aiMaxCardsInput}
                                        onChange={(e) => setAiMaxCardsInput(e.target.value)}
                                        disabled={!isEditingAiMaxCards}
                                        className={!isEditingAiMaxCards ? "bg-gray-50 dark:bg-gray-900/50 font-medium" : ""}
                                    />
                                    {isEditingAiMaxCards ? (
                                        <div className="flex gap-2">
                                            <Button
                                                onClick={handleSaveAiMaxCards}
                                                disabled={updateAiMaxCardsMutation.isPending}
                                                className="bg-green-600 hover:bg-green-700 text-white"
                                            >
                                                {updateAiMaxCardsMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                                            </Button>
                                            <Button variant="outline" onClick={() => {
                                                setIsEditingAiMaxCards(false);
                                                setAiMaxCardsInput(initialAiMaxCards);
                                            }}>
                                                Annuler
                                            </Button>
                                        </div>
                                    ) : (
                                        <Button variant="outline" onClick={() => setIsEditingAiMaxCards(true)}>
                                            Modifier
                                        </Button>
                                    )}
                                </div>
                            </CardContent>
                        </Card>

                        {/* Alertes et Notifications */}
                        <Card>
                            <CardHeader>
                                <div className="flex items-center gap-2">
                                    <Bell className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                                    <CardTitle>Notifications & Alertes</CardTitle>
                                </div>
                                <CardDescription>Gérez les emails que vous recevez de Pépite</CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                {prefsLoading ? (
                                    <div className="flex justify-center p-4"><Loader2 className="w-6 h-6 animate-spin text-amber-600 dark:text-amber-400" /></div>
                                ) : (
                                    <div className="space-y-4">
                                        <div className="flex items-center justify-between p-3 border rounded-lg bg-gray-50 dark:bg-gray-900/50 dark:border-gray-800">
                                            <div className="space-y-0.5">
                                                <Label className="text-base font-medium text-gray-900 dark:text-white cursor-pointer" htmlFor="pref_price_drops">Baisses de prix</Label>
                                                <p className="text-sm text-gray-500 dark:text-gray-400">Soyez alerté quand un article que vous avez liké baisse de prix.</p>
                                            </div>
                                            <Checkbox
                                                id="pref_price_drops"
                                                checked={preferences?.price_drops ?? true}
                                                onCheckedChange={(checked) => handleTogglePref('price_drops', checked)}
                                                className="data-[state=checked]:bg-amber-600 data-[state=checked]:border-amber-600"
                                            />
                                        </div>
                                        <div className="flex items-center justify-between p-3 border rounded-lg bg-gray-50 dark:bg-gray-900/50 dark:border-gray-800">
                                            <div className="space-y-0.5">
                                                <Label className="text-base font-medium text-gray-900 dark:text-white cursor-pointer" htmlFor="pref_restock">Alertes Restock</Label>
                                                <p className="text-sm text-gray-500 dark:text-gray-400">Recevez un email quand un article en rupture revient en stock.</p>
                                            </div>
                                            <Checkbox
                                                id="pref_restock"
                                                checked={preferences?.restock ?? true}
                                                onCheckedChange={(checked) => handleTogglePref('restock', checked)}
                                                className="data-[state=checked]:bg-amber-600 data-[state=checked]:border-amber-600"
                                            />
                                        </div>
                                        <div className="flex items-center justify-between p-3 border rounded-lg bg-gray-50 dark:bg-gray-900/50 dark:border-gray-800">
                                            <div className="space-y-0.5">
                                                <Label className="text-base font-medium text-gray-900 dark:text-white cursor-pointer" htmlFor="pref_new_from_subscribed">Nouveautés des marques</Label>
                                                <p className="text-sm text-gray-500 dark:text-gray-400">Soyez le premier à découvrir les nouveaux articles de vos marques préférées.</p>
                                            </div>
                                            <Checkbox
                                                id="pref_new_from_subscribed"
                                                checked={preferences?.new_from_subscribed ?? true}
                                                onCheckedChange={(checked) => handleTogglePref('new_from_subscribed', checked)}
                                                className="data-[state=checked]:bg-amber-600 data-[state=checked]:border-amber-600"
                                            />
                                        </div>
                                        <div className="flex flex-col gap-3 p-3 border rounded-lg bg-gray-50 dark:bg-gray-900/50 dark:border-gray-800">
                                            <div className="flex items-center justify-between">
                                                <div className="space-y-0.5">
                                                    <Label className="text-base font-medium text-gray-900 dark:text-white cursor-pointer" htmlFor="pref_push_enabled">Notifications Push</Label>
                                                    <p className="text-sm text-gray-500 dark:text-gray-400">Recevez des alertes instantanées sur votre appareil (navigateur/mobile).</p>
                                                </div>
                                                <Checkbox
                                                    id="pref_push_enabled"
                                                    checked={preferences?.push_enabled ?? false}
                                                    onCheckedChange={(checked) => handleTogglePush(checked)}
                                                    className="data-[state=checked]:bg-amber-600 data-[state=checked]:border-amber-600"
                                                />
                                            </div>
                                            {preferences?.push_enabled && (
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    className="w-full mt-1 border-amber-200 text-amber-600 hover:bg-amber-50 dark:border-amber-800 dark:text-amber-400 dark:hover:bg-amber-900/30"
                                                    onClick={handleTestPush}
                                                >
                                                    Tester la notification push
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </CardContent>
                        </Card>

                        {/* Marques Suivies */}
                        <Card>
                            <CardHeader>
                                <CardTitle>Mes Marques Suivies</CardTitle>
                                <CardDescription>Vous êtes abonné aux nouveautés de ces marques</CardDescription>
                            </CardHeader>
                            <CardContent>
                                {subsLoading ? (
                                    <div className="flex justify-center p-4"><Loader2 className="w-6 h-6 animate-spin text-amber-600 dark:text-amber-400" /></div>
                                ) : subscriptions?.length > 0 ? (
                                    <div className="grid gap-3 sm:grid-cols-2">
                                        {subscriptions.map(sub => (
                                            <div key={sub.id} className="flex items-center justify-between p-3 border rounded-lg hover:border-amber-200 dark:hover:border-amber-800 transition-colors">
                                                <span className="font-medium">{sub.name}</span>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    className="text-red-500 hover:text-red-600 hover:bg-red-50"
                                                    onClick={() => unsubscribeMutation.mutate(sub.id)}
                                                    disabled={unsubscribeMutation.isPending}
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </Button>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="text-center py-6 text-gray-500 dark:text-gray-400 border rounded-lg bg-gray-50 dark:bg-gray-900/50 dark:border-gray-800">
                                        <p>Vous ne suivez aucune marque pour le moment.</p>
                                        <Button variant="link" className="text-amber-600 dark:text-amber-400 mt-2" onClick={() => navigate('/catalog')}>
                                            Explorer les marques
                                        </Button>
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </div>
                </TabsContent>
            </Tabs>

            {/* Password Reset Dialog */}
            <Dialog open={showPasswordResetDialog} onOpenChange={setShowPasswordResetDialog}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Réinitialiser le mot de passe</DialogTitle>
                        <DialogDescription>
                            Voulez-vous recevoir un email pour réinitialiser votre mot de passe ?
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowPasswordResetDialog(false)}>Annuler</Button>
                        <Button onClick={confirmPasswordReset}>Confirmer</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Guest Reset Dialog */}
            <Dialog open={showGuestResetDialog} onOpenChange={setShowGuestResetDialog}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Réinitialiser les préférences</DialogTitle>
                        <DialogDescription>
                            Êtes-vous sûr ? Cela effacera tous vos likes et l'historique de cette session invité.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowGuestResetDialog(false)}>Annuler</Button>
                        <Button variant="destructive" onClick={handleGuestResetConfirm}>Confirmer</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="text-red-600">Supprimer votre compte ?</DialogTitle>
                        <DialogDescription>
                            Cette action est irréversible. Votre compte sera <strong>définitivement supprimé dans 7 jours</strong>.
                            <br /><br />
                            Pendant cette période, vous pourrez annuler la suppression à tout moment en vous reconnectant.
                            <br /><br />
                            Un email de confirmation vous sera envoyé.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>Annuler</Button>
                        <Button
                            variant="destructive"
                            onClick={handleDeleteAccount}
                            disabled={isLoading}
                        >
                            {isLoading ? "Traitement..." : "Confirmer la suppression"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={showHistoryResetDialog} onOpenChange={setShowHistoryResetDialog}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="text-amber-700 dark:text-amber-300">Réinitialiser l'historique ?</DialogTitle>
                        <DialogDescription>
                            Vous allez masquer toutes vos interactions passées (likes, swipes). Votre compte apparaîtra comme neuf.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowHistoryResetDialog(false)}>Annuler</Button>
                        <Button
                            className="bg-amber-600 hover:bg-amber-700 text-white"
                            onClick={handleResetHistory}
                            disabled={isLoading}
                        >
                            {isLoading ? "Traitement..." : "Confirmer la réinitialisation"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
            {/* Email Change Confirmation Dialog */}
            <Dialog open={showEmailConfirmDialog} onOpenChange={setShowEmailConfirmDialog}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="text-orange-600">Confirmer le changement d'email ?</DialogTitle>
                        <DialogDescription>
                            Vous êtes sur le point de changer votre adresse email pour <strong>{pendingEmail}</strong>.
                            <br /><br />
                            ⚠️ <strong>Attention :</strong>
                            <ul className="list-disc pl-5 mt-2 space-y-1">
                                <li>Vous serez <strong>déconnecté immédiatement</strong>.</li>
                                <li>Un email de vérification sera envoyé à votre nouvelle adresse.</li>
                                <li>Vous devrez valider ce lien avant de pouvoir vous reconnecter.</li>
                            </ul>
                            <br />
                            Êtes-vous sûr de vouloir continuer ?
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowEmailConfirmDialog(false)}>Annuler</Button>
                        <Button
                            className="bg-orange-600 hover:bg-orange-700 text-white"
                            onClick={confirmEmailChange}
                            disabled={updateProfileMutation.isPending}
                        >
                            {updateProfileMutation.isPending ? "Traitement..." : "J'accepte et je me déconnecte"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
// Theme switcher card for the Preferences tab
function ThemeCard() {
    const { mode, resolvedTheme, setMode } = useTheme();

    const options = [
        { value: 'system', label: 'Système', icon: Monitor, description: 'Suit le thème de votre appareil' },
        { value: 'light', label: 'Clair', icon: Sun, description: 'Toujours en mode clair' },
        { value: 'dark', label: 'Sombre', icon: Moon, description: 'Toujours en mode sombre' },
    ];

    return (
        <Card>
            <CardHeader>
                <div className="flex items-center gap-2">
                    {resolvedTheme === 'dark' ? (
                        <Moon className="w-5 h-5 text-amber-400" />
                    ) : (
                        <Sun className="w-5 h-5 text-amber-600" />
                    )}
                    <CardTitle>Apparence</CardTitle>
                </div>
                <CardDescription>
                    Choisissez le thème de l'interface — actuellement en mode <strong>{resolvedTheme === 'dark' ? 'sombre' : 'clair'}</strong>
                </CardDescription>
            </CardHeader>
            <CardContent>
                <div className="grid grid-cols-3 gap-2 p-1 bg-gray-100 dark:bg-[hsl(225,12%,12%)] rounded-xl">
                    {options.map((opt) => {
                        const isActive = mode === opt.value;
                        const Icon = opt.icon;
                        return (
                            <button
                                key={opt.value}
                                id={`theme-toggle-${opt.value}`}
                                onClick={() => setMode(opt.value)}
                                className={`
                                    relative flex flex-col items-center gap-1.5 px-3 py-3 rounded-lg
                                    text-sm font-medium transition-all duration-200
                                    focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500
                                    ${isActive
                                        ? 'bg-white dark:bg-[hsl(225,14%,14%)] text-amber-600 dark:text-amber-400 shadow-sm'
                                        : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-white/50 dark:hover:bg-white/5'
                                    }
                                `}
                            >
                                <Icon className={`w-5 h-5 ${isActive ? 'text-amber-600 dark:text-amber-400' : ''}`} />
                                <span>{opt.label}</span>
                                {isActive && (
                                    <span className="absolute -bottom-0.5 left-1/2 -translate-x-1/2 w-6 h-0.5 rounded-full bg-gradient-to-r from-amber-600 to-amber-700" />
                                )}
                            </button>
                        );
                    })}
                </div>
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-3 text-center">
                    {options.find(o => o.value === mode)?.description}
                </p>
            </CardContent>
        </Card>
    );
}

// Simple internal icon for cancel if needed or use Lucide's X
function XLogIcon(props) {
    return (
        <svg
            {...props}
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        >
            <path d="M18 6 6 18" />
            <path d="m6 6 18 18" />
        </svg>
    )
}
